import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Input, InputNumber, Button, Space, Descriptions, message, Select, Tag, Divider, Alert, Modal, Drawer, Table, Upload } from 'antd';
import { useLocation } from 'react-router-dom';
import request from '../../../utils/request';
import { getCustomerList, getProjectList } from '../../../services/project';
import { getAllWarehouses } from '../../../services/warehouse';

const RatePreviewPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const location = useLocation();
  // QA 用例管理（本地）
  type RateCase = {
    id: string;
    name: string;
    params: any; // customer_id/project_id/warehouse_id/instruction_template_code/workorder_template_code/operation_type + metrics
    expected?: { rule_system_code?: string; amount?: number; currency?: string };
    notes?: string;
  };
  const LS_KEY = 'ratePreview.cases.v1';
  const [cases, setCases] = useState<RateCase[]>([]);
  const [caseOpen, setCaseOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm] = Form.useForm();

  // 读取URL参数用于预填（来自费率规则列表或其他入口）
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search);
      const initial: any = {};
      ['customer_id','project_id','warehouse_id'].forEach(k => {
        const v = sp.get(k); if (v) initial[k] = Number(v);
      });
      ['instruction_template_code','workorder_template_code','operation_type'].forEach(k => {
        const v = sp.get(k); if (v) initial[k] = v;
      });
      // 常见度量预填
      ['planned_qty','label_count','box_count','work_time_min'].forEach(k => {
        const v = sp.get(k); if (v) initial[k] = Number(v);
      });
      if (Object.keys(initial).length > 0) form.setFieldsValue(initial);
    } catch {}
  }, [location.search]);

  // 载入/持久化用例
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const list = JSON.parse(raw);
        if (Array.isArray(list)) setCases(list);
      }
    } catch {}
  }, []);
  const persistCases = (list: RateCase[]) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch {}
    setCases(list);
  };

  // 新增：客户/项目/规则候选
  const [customerOptions, setCustomerOptions] = useState<Array<{ value:number; label:string }>>([]);
  const [projectOptions, setProjectOptions] = useState<Array<{ value:number; label:string }>>([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [rules, setRules] = useState<any[]>([]);
  const [ruleId, setRuleId] = useState<number | undefined>(undefined);
  const chosenRule = useMemo(() => rules.find((r:any)=> Number(r.id)===Number(ruleId)), [rules, ruleId]);
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ value:number; label:string }>>([]);
  const [warehouseLoading, setWarehouseLoading] = useState(false);

  const loadCustomers = async () => {
    try {
      const list:any[] = (await getCustomerList({ onlyMain: true })) || [];
      setCustomerOptions(list.map((c:any) => {
        const code = c?.customer_code || c?.profile?.customer_code || '';
        const name = c?.name || c?.username || '';
        return { value: Number(c.id), label: code ? `${name} · ${code}` : name };
      }));
    } catch {}
  };

  const loadProjects = async (cid?: number) => {
    if (!cid) { setProjectOptions([]); return; }
    setProjectLoading(true);
    try {
      const pageSize = 200; let page=1; const all:any[] = [];
      while (true) {
        const resp = await getProjectList({ page, page_size: pageSize, search: '', customer: cid } as any);
        const results = Array.isArray((resp as any)?.data?.results) ? (resp as any).data.results : [];
        if (!results || results.length===0) break;
        all.push(...results);
        if (results.length < pageSize) break;
        page += 1; if (page>1000) break;
      }
      setProjectOptions(all.map((p:any)=>({ value:Number(p.id), label:`${p.project_code || p.id} - ${p.name || ''}`})));
    } finally { setProjectLoading(false); }
  };

  const loadEffectiveRules = async () => {
    try {
      const v = form.getFieldsValue();
      const params:any = {};
      if (v.customer_id) params.customer_id = v.customer_id;
      if (v.project_id) params.project_id = v.project_id;
      const resp = await request.get('/api/v1/billing/billing-rules/effective/', { params });
      const list = Array.isArray(resp?.data) ? resp.data : [];
      setRules(list);
      setRuleId(list[0]?.id);
    } catch {
      setRules([]); setRuleId(undefined);
    }
  };

  const loadWarehouses = async () => {
    if (warehouseOptions.length > 0) return;
    setWarehouseLoading(true);
    try {
      const list = await getAllWarehouses();
      const opts = (list || []).map((w:any) => {
        const code = w.code || w.warehouse_code || w.system_code || w.id;
        const name = w.name || '';
        return { value: Number(w.id), label: `${name} · ${code}` };
      });
      setWarehouseOptions(opts);
    } catch {
      setWarehouseOptions([]);
    } finally {
      setWarehouseLoading(false);
    }
  };

  const [insTplOptions, setInsTplOptions] = useState<Array<{ value:string; label:string }>>([]);
  const [insTplLoading, setInsTplLoading] = useState(false);
  const [woTplOptions, setWoTplOptions] = useState<Array<{ value:string; label:string }>>([]);
  const [woTplLoading, setWoTplLoading] = useState(false);

  const loadInstructionTemplates = async () => {
    if (insTplOptions.length > 0) return;
    setInsTplLoading(true);
    const endpoints = ['/api/v1/instruction/instruction-templates/', '/api/v1/instruction/templates/'];
    try {
      let list:any[] = [];
      for (const ep of endpoints) {
        try {
          const resp = await request.get(ep, { params: { is_active: true, all: 1 } });
          const data = resp?.data;
          list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          if (list && list.length) break;
        } catch {}
      }
      const opts = (list || []).map((t:any) => ({ value: String(t.system_code || t.code || ''), label: `${t.name || '-'} · ${(t.system_code || t.code || '')}` })).filter(o => !!o.value);
      setInsTplOptions(opts);
    } finally { setInsTplLoading(false); }
  };

  const loadWorkOrderTemplates = async () => {
    if (woTplOptions.length > 0) return;
    setWoTplLoading(true);
    const endpoints = ['/api/v1/workorders/templates/', '/api/v1/workorders/work-order-templates/'];
    try {
      let list:any[] = [];
      for (const ep of endpoints) {
        try {
          const resp = await request.get(ep, { params: { is_active: true, all: 1 } });
          const data = resp?.data;
          list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          if (list && list.length) break;
        } catch {}
      }
      const opts = (list || []).map((t:any) => ({ value: String(t.code || ''), label: `${t.name || '-'} · ${(t.code || '')}` })).filter(o => !!o.value);
      setWoTplOptions(opts);
    } finally { setWoTplLoading(false); }
  };

  const onPreview = async () => {
    try {
      const v = await form.validateFields();
      setLoading(true);
      const metrics:any = {};
      const unitMap:any = { PER_ITEM: 'planned_qty', PER_BOX: 'box_count', PER_PALLET: 'pallet_count', PER_MINUTE: 'work_time_min' };
      const tierMap:any = { QUANTITY: 'planned_qty', WEIGHT_G: 'weight_g', WEIGHT_KG: 'weight_kg', VOLUME_CUFT: 'volume_cuft', VOLUME_CBM: 'volume_cbm', VOLUME_CC: 'volume_cc', AGE_DAY: 'age_day' };
      if (chosenRule) {
        if (chosenRule.rule_type === 'tiered') {
          const k = tierMap[String(chosenRule.tier_basis) || 'QUANTITY'] || 'planned_qty';
          metrics[k] = v.dynamic_value != null ? v.dynamic_value : v.planned_qty;
        } else {
          const k = unitMap[String(chosenRule.billing_unit)] || 'planned_qty';
          metrics[k] = v.dynamic_value != null ? v.dynamic_value : v.planned_qty;
        }
      } else {
        if (v.planned_qty != null) metrics.planned_qty = v.planned_qty;
        if (v.label_count != null) metrics.label_count = v.label_count;
        if (v.box_count != null) metrics.box_count = v.box_count;
        if (v.work_time_min != null) metrics.work_time_min = v.work_time_min;
      }
      const resp = await request.post('/api/v1/billing/billing-records/preview/', {
        customer_id: v.customer_id || undefined,
        project_id: v.project_id || undefined,
        warehouse_id: v.warehouse_id || undefined,
        instruction_template_code: v.instruction_template_code || undefined,
        workorder_template_code: v.workorder_template_code || undefined,
        operation_type: v.operation_type || undefined,
        metrics,
      });
      const data = resp?.data;
      setResult(data);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '试算失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存为用例
  const openSaveAsCase = () => {
    try {
      const v = form.getFieldsValue();
      const defId = `CASE-${Date.now()}`;
      const defName = `${v.operation_type || v.instruction_template_code || '用例'}`;
      saveForm.setFieldsValue({ id: defId, name: defName, notes: '' });
      setSaveOpen(true);
    } catch { setSaveOpen(true); }
  };
  const doSaveCase = async () => {
    try {
      const meta = await saveForm.validateFields();
      const v = form.getFieldsValue();
      const params: any = {
        customer_id: v.customer_id || undefined,
        project_id: v.project_id || undefined,
        warehouse_id: v.warehouse_id || undefined,
        instruction_template_code: v.instruction_template_code || undefined,
        workorder_template_code: v.workorder_template_code || undefined,
        operation_type: v.operation_type || undefined,
      };
      // 收集常用指标
      ['planned_qty','label_count','box_count','work_time_min','dynamic_value'].forEach(k => {
        if (v[k] != null) params[k] = v[k];
      });
      const expected = result ? {
        rule_system_code: result?.rule_system_code || result?.rule_code || result?.rule_id,
        amount: (result?.amount != null ? Number(result.amount) : undefined),
        currency: result?.currency,
      } : undefined;
      const next: RateCase = { id: String(meta.id), name: meta.name, params, expected, notes: meta.notes };
      const list = [...cases.filter(c => c.id !== next.id), next];
      persistCases(list);
      message.success('已保存为用例');
      setSaveOpen(false);
    } catch (e:any) {
      if (e?.errorFields) return;
      message.error('保存失败');
    }
  };
  const fillFromCase = (c: RateCase) => {
    const p = c.params || {};
    form.setFieldsValue({
      customer_id: p.customer_id,
      project_id: p.project_id,
      warehouse_id: p.warehouse_id,
      instruction_template_code: p.instruction_template_code,
      workorder_template_code: p.workorder_template_code,
      operation_type: p.operation_type,
      planned_qty: p.planned_qty,
      label_count: p.label_count,
      box_count: p.box_count,
      work_time_min: p.work_time_min,
      dynamic_value: p.dynamic_value,
    });
    message.success('已填充用例参数');
  };
  const removeCase = (id:string) => {
    persistCases(cases.filter(c => c.id !== id));
  };
  const exportCases = () => {
    try {
      const blob = new Blob([JSON.stringify(cases, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'rate_cases.json';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { message.error('导出失败'); }
  };
  const importCases = async () => {
    Modal.confirm({
      title: '导入用例（JSON）',
      icon: null,
      content: (
        <Input.TextArea rows={8} placeholder="粘贴用例JSON数组" id="rate-cases-import-textarea" />
      ),
      width: 640,
      okText: '导入',
      onOk: () => {
        try {
          const el:any = document.getElementById('rate-cases-import-textarea');
          const text = String(el?.value || '');
          const arr = JSON.parse(text);
          if (!Array.isArray(arr)) throw new Error('格式需为数组');
          persistCases(arr);
          message.success('已导入用例');
        } catch (e:any) {
          message.error(e?.message || '导入失败');
          throw e;
        }
      }
    });
  };

  return (
    <div className="list-page-container">
      <Card>
        <Alert
          type="info"
          showIcon
          message="使用说明"
          description={(
            <div>
              <div>· 本页用于在不改动真实数据的情况下，快速验证“当前生效规则在给定上下文与口径下的计价结果”。</div>
              <div>· 步骤：选择客户/项目 → 点击“载入生效规则” → 选择具体规则 → 输入该规则所需口径数值 → 点击“试算”。</div>
              <div>· 未选择规则时，可用通用口径（预计数量/标签张数/箱数/作业时长）做命中链与通道可用性校验。</div>
              <div style={{ marginTop: 6 }}>· 适用场景（示例）：客户价高/算错排障、上线前验证、运维调参后即时确认、快速报价、争议复盘、梯度边界验证、无规则回退检查等。</div>
              <div style={{ marginTop: 6 }}>· “保存为用例”的作用：将当前参数与试算结果固化为一条用例，便于后续在“用例集”中导入/导出、快速填充并复跑试算，与工单计费结果进行勾对验证。</div>
              <div style={{ marginTop: 6 }}>· 选择“指令模板/工单模板”的作用：
                1) 精准匹配包含模板维度的规则，避免误命中；
                2) 推导计费口径（operation_type/计费单位/梯度维度），按规则动态渲染输入；
                3) 当无规则命中时，支持回退到模板快照单价；
                4) 复现真实指令/工单上下文，保证试算结果与现场一致。
              </div>
            </div>
          )}
          style={{ marginBottom: 12 }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>费率试算</div>
          <Space>
            <Button onClick={loadEffectiveRules}>载入生效规则</Button>
            <Button type="primary" onClick={onPreview} loading={loading}>试算</Button>
            <Button onClick={() => { form.resetFields(); setRules([]); setRuleId(undefined); setResult(null); }}>重置</Button>
            <Button onClick={openSaveAsCase}>保存为用例</Button>
            <Button onClick={() => setCaseOpen(true)}>用例集</Button>
          </Space>
        </div>
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={6}><Form.Item name="customer_id" label="所属客户">
              <Select
                allowClear showSearch placeholder="选择客户"
                options={customerOptions}
                onFocus={() => loadCustomers()}
                onChange={(val)=> { form.setFieldsValue({ project_id: undefined }); setProjectOptions([]); if (val) loadProjects(Number(val)); }}
              />
            </Form.Item></Col>
            <Col span={6}><Form.Item name="project_id" label="项目">
              <Select allowClear showSearch placeholder="先选择客户"
                disabled={!form.getFieldValue('customer_id')}
                options={projectOptions}
              />
            </Form.Item></Col>
            <Col span={6}><Form.Item name="warehouse_id" label="仓库">
              <Select allowClear showSearch placeholder="选择仓库"
                options={warehouseOptions}
                loading={warehouseLoading}
                onFocus={() => loadWarehouses()}
                filterOption={(input, option) => (option?.label as string).toLowerCase().includes(String(input).toLowerCase())}
              />
            </Form.Item></Col>
            <Col span={6}><Form.Item name="operation_type" label="操作类型代码"><Input placeholder="如 RELABEL" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="instruction_template_code" label="指令模板">
              <Select allowClear showSearch placeholder="选择指令模板"
                options={insTplOptions}
                loading={insTplLoading}
                onFocus={() => loadInstructionTemplates()}
                filterOption={(input, option) => (option?.label as string).toLowerCase().includes(String(input).toLowerCase())}
              />
            </Form.Item></Col>
            <Col span={8}><Form.Item name="workorder_template_code" label="工单模板">
              <Select allowClear showSearch placeholder="选择工单模板"
                options={woTplOptions}
                loading={woTplLoading}
                onFocus={() => loadWorkOrderTemplates()}
                filterOption={(input, option) => (option?.label as string).toLowerCase().includes(String(input).toLowerCase())}
              />
            </Form.Item></Col>
          </Row>
          {rules.length > 0 && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              <Row gutter={12}>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>选择生效规则</div>
                  <Select style={{ width: '100%' }} value={ruleId as any} onChange={(v)=> setRuleId(Number(v))}
                    options={rules.map((r:any)=>({ value:r.id, label:(
                      <div style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
                        <span>{r.display_name || r.operation_type_name}</span>
                        <span>
                          <Tag color={r.rule_type==='tiered'?'blue':'default'}>{r.rule_type}</Tag>
                          {r.unit_price_display ? <Tag>{r.unit_price_display}</Tag> : null}
                          {r.currency ? <Tag>{r.currency}</Tag> : null}
                        </span>
                      </div>
                    ) }))}
                  />
                </Col>
              </Row>
              {chosenRule && (
                <Row gutter={12} style={{ marginTop: 8 }}>
                  <Col span={12}>
                    <div style={{ marginBottom: 4 }}>
                      {chosenRule.rule_type === 'tiered' ? `口径（${chosenRule.tier_basis || 'QUANTITY'}）` : `口径（${chosenRule.billing_unit || 'PER_ITEM'}）`}
                    </div>
                    <Form.Item name="dynamic_value" style={{ marginBottom: 0 }}>
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入本规则所需口径的数值" />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
          )}
          {!chosenRule && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              <Row gutter={12}>
                <Col span={6}><Form.Item name="planned_qty" label="预计数量"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={6}><Form.Item name="label_count" label="标签张数"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={6}><Form.Item name="box_count" label="箱数"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={6}><Form.Item name="work_time_min" label="作业时长(分钟)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              </Row>
              <div style={{ color:'#999' }}>未选择规则时，使用通用口径进行基础验证；选择规则后按规则口径输入。</div>
            </>
          )}
        </Form>
        <div style={{ height: 12 }} />
        <Card size="small" title="试算结果">
          {!result && <div style={{ color: '#999' }}>请填写参数后点击“试算”。</div>}
          {result && (
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="命中规则ID">{result.rule_id ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="口径">{result.metric_key ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="单价">{result.unit_price ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="币种">{result.currency ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="数量">{result.qty ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="金额">{result.amount ?? '-'}</Descriptions.Item>
              {result.rule_type === 'tiered' && (
                <Descriptions.Item label="命中梯度" span={2}>
                  {result.matched_tier ? (
                    <span>
                      区间[{result.matched_tier.min_value ?? '0'}, {result.matched_tier.max_value ?? '∞'}) → 单价 {result.matched_tier.unit_price}
                    </span>
                  ) : <span>未命中梯度，回退规则单价</span>}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </Card>
      </Card>

      {/* 保存为用例 */}
      <Modal
        title="保存为用例"
        open={saveOpen}
        onCancel={() => setSaveOpen(false)}
        onOk={doSaveCase}
        okText="保存"
      >
        <Form form={saveForm} layout="vertical">
          <Form.Item name="id" label="用例ID" rules={[{ required: true }]}>
            <Input placeholder="唯一标识（可使用英文/数字/下划线）" />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input placeholder="如 RELABEL-100张" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="可填写期望说明/口径备注" />
          </Form.Item>
          <Alert type="info" showIcon message="提示" description="当前已计算的试算结果（若有）会自动写入该用例的期望金额/币种/规则编码，便于后续勾对。" />
        </Form>
      </Modal>

      {/* 用例集 */}
      <Drawer title="用例集" open={caseOpen} onClose={() => setCaseOpen(false)} width={720} extra={
        <Space>
          <Button onClick={exportCases}>导出</Button>
          <Button onClick={importCases}>导入</Button>
        </Space>
      }>
        <Table
          size="small"
          rowKey={(r:any, i?:number) => String(r?.id || i)}
          dataSource={cases}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 140 },
            { title: '名称', dataIndex: 'name', width: 180 },
            { title: '上下文', render: (_:any, r:any) => {
              const p = r.params || {};
              const ctx = [p.customer_id && `客户:${p.customer_id}`, p.project_id && `项目:${p.project_id}`, p.warehouse_id && `仓库:${p.warehouse_id}`, p.instruction_template_code && `指令:${p.instruction_template_code}`, p.workorder_template_code && `工单:${p.workorder_template_code}`, p.operation_type && `动作:${p.operation_type}`].filter(Boolean).join(' / ');
              return ctx || '-';
            } },
            { title: '期望', render: (_:any, r:any) => {
              const e = r.expected || {};
              if (!e.amount && !e.rule_system_code) return <span style={{ color:'#999' }}>未设</span>;
              return <span>{e.rule_system_code ? `${e.rule_system_code} · ` : ''}{e.amount != null ? e.amount : '-'}{e.currency ? ` ${e.currency}` : ''}</span>;
            } },
            { title: '操作', width: 200, render: (_:any, r:any) => (
              <Space>
                <Button size="small" onClick={() => fillFromCase(r)}>填充</Button>
                <Button size="small" danger onClick={() => removeCase(r.id)}>删除</Button>
              </Space>
            ) },
          ] as any}
        />
      </Drawer>
    </div>
  );
};

export default RatePreviewPage;


